import React, { useState, useEffect } from "react";
import Barcode from "react-barcode";
import { useTranslation } from "../context/LanguageContext";
import {
  cleanUpc,
  getBarcodeFormat,
  getBarcodeSizing,
  isValidUpcInput,
} from "./Barcode.utils";

interface BarcodeProps {
  /** The UPC code to generate a barcode for */
  upc: string;
  /** Width of the barcode (default: 2) */
  width?: number;
  /** Height of the barcode (default: 100) */
  height?: number;
  /** Whether to display the text below the barcode (default: true) */
  displayValue?: boolean;
  /** Font size for the text (default: 20) */
  fontSize?: number;
  /** Additional CSS classes */
  className?: string;
  /** Background color (default: #ffffff) */
  background?: string;
  /** Line color (default: #000000) */
  lineColor?: string;
  /** Whether the barcode should be clickable to open larger view (default: true) */
  clickable?: boolean;
  /** Force CODE128 format (useful for non-standard identifiers like TCIN) */
  forceCode128?: boolean;
}

const BarcodeComponent: React.FC<BarcodeProps> = ({
  upc,
  width = 2,
  height = 100,
  displayValue = true,
  fontSize = 20,
  className = "",
  background = "#ffffff",
  lineColor = "#000000",
  clickable = true,
  forceCode128 = false,
}) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsModalOpen(false);
      }
    };

    if (isModalOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  const handleBarcodeClick = () => {
    if (clickable) {
      setIsModalOpen(true);
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleModalContentClick = (e: React.MouseEvent) => {
    // Prevent modal from closing when clicking on the barcode itself
    e.stopPropagation();
  };

  // Validate UPC
  if (!isValidUpcInput(upc)) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 border border-gray-300 rounded p-4 ${className}`}
      >
        <span className="text-gray-500 text-sm">Invalid UPC</span>
      </div>
    );
  }

  // Clean the UPC (remove any non-numeric characters)
  const cleanedUpc = cleanUpc(upc);

  if (cleanedUpc.length === 0) {
    return (
      <div
        className={`flex items-center justify-center bg-gray-100 border border-gray-300 rounded p-4 ${className}`}
      >
        <span className="text-gray-500 text-sm">Invalid UPC Format</span>
      </div>
    );
  }

  // Determine the best format based on length
  const format = forceCode128 ? "CODE128" : getBarcodeFormat(cleanedUpc);

  const BarcodeDisplay = ({
    isLarge = false,
    onPointerDown,
  }: {
    isLarge?: boolean;
    onPointerDown?: () => void;
  }) => {
    try {
      const sizing = getBarcodeSizing({
        isLarge,
        width,
        height,
        fontSize,
      });

      return (
        <div
          className={`inline-block ${isLarge ? "" : className} ${
            clickable && !isLarge
              ? "cursor-pointer hover:opacity-80 transition-opacity"
              : ""
          }`}
          onPointerDown={onPointerDown}
          title={
            clickable && !isLarge
              ? t("productSearch.clickToViewLarger")
              : undefined
          }
        >
          <Barcode
            value={cleanedUpc}
            format={format}
            width={sizing.width}
            height={sizing.height}
            displayValue={displayValue}
            fontSize={sizing.fontSize}
            background={background}
            lineColor={lineColor}
          />
        </div>
      );
    } catch (error) {
      console.error("Error generating barcode:", error);
      return (
        <div
          className={`flex items-center justify-center bg-gray-100 border border-gray-300 rounded p-4 ${
            isLarge ? "min-w-[300px] min-h-[200px]" : className
          }`}
        >
          <span className="text-gray-500 text-sm">
            Barcode Generation Error
          </span>
        </div>
      );
    }
  };

  return (
    <>
      {/* Regular barcode */}
      <BarcodeDisplay onPointerDown={handleBarcodeClick} />

      {/* Modal for larger barcode */}
      {isModalOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onPointerDown={handleModalClose}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-auto relative"
            onPointerDown={handleModalContentClick}
          >
            {/* Close button */}
            <button
              onPointerDown={handleModalClose}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
              aria-label={t("productSearch.closeBarcodeView")}
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>

            {/* Modal content */}
            <div className="p-8 text-center">
              <h3 className="text-lg font-semibold text-gray-900 mb-6">
                {t("productSearch.productBarcode")}
              </h3>
              <div className="flex justify-center">
                <BarcodeDisplay isLarge={true} />
              </div>
              <p className="text-gray-600 text-sm mt-4">UPC: {cleanedUpc}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BarcodeComponent;
